import * as React from 'react'
import {
  ArrowsDownUp,
  SortAscending,
  SortDescending,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Table — composable, presentational table primitives (`TableRoot` / `TableHeader`
 * / `TableBody` / `TableHead` / `TableRow` / `TableCell`) with native `<table>`
 * semantics, wired to First Bite tokens. Bring your own rows (and your own data
 * engine — TanStack, plain arrays, whatever); these only render and style.
 *
 *   <TableRoot size="sm">
 *     <TableHeader>
 *       <TableRow>
 *         <TableHead>Name</TableHead>
 *         <TableHead pin="right">{/* actions ⋯ *\/}</TableHead>
 *       </TableRow>
 *     </TableHeader>
 *     <TableBody>
 *       <TableRow selected>
 *         <TableCell>Acme</TableCell>
 *         <TableCell pin="right">…</TableCell>
 *       </TableRow>
 *     </TableBody>
 *   </TableRoot>
 *
 * `size` (`xs` | `sm` | `md`, default `md`) flows from `TableRoot` to every cell
 * via context; `headerSize` sets the header row independently (e.g. a small header
 * over medium rows). Cells stay on one line (ellipsis when constrained) unless
 * `truncate={false}` (dates, deals/IDs). `fullBleed` drops the rounded corners +
 * side borders. `stickyHeader`
 * pins the header row while the body scrolls (give `TableRoot` a `max-h-*`).
 *
 * Column features:
 * - `pin="left" | "right"` on a `TableHead`/`TableCell` freezes that column against
 *   the matching edge during horizontal scroll (e.g. a left checkbox column or a
 *   right actions column). Pin the head and every matching cell.
 * - `resizable` on `TableRoot` switches the table to fixed layout and lets columns
 *   be resized — drag the header's right edge or focus the handle and press ←/→
 *   (Shift = larger step). Give each resizable `TableHead` a `columnId` and an
 *   optional `minWidth` (default 80) / `defaultWidth`; pinned columns don't resize.
 *   Persist widths via `columnWidths`/`onColumnWidthsChange` (or `defaultColumnWidths`).
 *
 * Column headers can be sortable (`sortable` + `sortDirection` + `onSort` render a
 * caret); cell links render in brand green (no underline) to match DS2026 entity
 * links. Rows highlight on hover; `selected` tints a row with the brand accent.
 * Selection checkboxes and row-action menus are composed from `Checkbox` and
 * `DropdownMenu` at the call site — the table stays data-agnostic.
 */

type TableSize = 'xs' | 'sm' | 'md'
type Pin = 'left' | 'right'

interface TableContextValue {
  size: TableSize
  headerSize: TableSize
  stickyHeader: boolean
  resizable: boolean
  widths: Record<string, number>
  setColumnWidth: (id: string, width: number) => void
}

const TableContext = React.createContext<TableContextValue>({
  size: 'md',
  headerSize: 'md',
  stickyHeader: false,
  resizable: false,
  widths: {},
  setColumnWidth: () => {},
})

/** The enclosing table's body `size` — e.g. to match a selection `Checkbox`. */
export const useTableSize = (): TableSize =>
  React.useContext(TableContext).size

/** The enclosing table's header-row `size` (may differ from the body `size`). */
export const useTableHeaderSize = (): TableSize =>
  React.useContext(TableContext).headerSize

/**
 * Whether the enclosing table uses fixed (resizable) layout. Control columns need
 * this: fixed layout honors an exact `w-*`, while auto layout stretches it and a
 * column must instead shrink to its content (`w-[1%]`).
 */
export const useTableResizable = (): boolean =>
  React.useContext(TableContext).resizable

type TableSection = 'head' | 'body'
const TableSectionContext = React.createContext<TableSection>('body')

const sizeCfg = {
  xs: { head: 'px-3 py-2', cell: 'px-3 py-2 text-xs' },
  sm: { head: 'px-3.5 py-2.5', cell: 'px-3.5 py-2.5 text-sm' },
  md: { head: 'px-4 py-3', cell: 'px-4 py-3.5 text-sm' },
} as const

const DEFAULT_MIN_WIDTH = 80

const pinClass = (pin: Pin | undefined): string =>
  pin === 'left' ? 'sticky left-0' : pin === 'right' ? 'sticky right-0' : ''

// A pinned column casts a soft shadow over scrolled content — but only while
// there's content hidden on that side (no static divider line), matching DS2026.
// Spread is 0 (not negative) so each cell's shadow spans its full height and
// blends into the next — a continuous vertical edge, not a per-row segment.
const pinShadow = (pin: Pin | undefined): string =>
  pin === 'left'
    ? 'transition-shadow group-data-[can-scroll-left]/tscroll:shadow-[4px_0_5px_0_rgba(16,24,40,0.08)]'
    : pin === 'right'
      ? 'transition-shadow group-data-[can-scroll-right]/tscroll:shadow-[-4px_0_5px_0_rgba(16,24,40,0.08)]'
      : ''

export interface TableRootProps
  extends React.TableHTMLAttributes<HTMLTableElement> {
  size?: TableSize
  /** Header-row density. Defaults to `size`; set independently to mix densities. */
  headerSize?: TableSize
  /** Drop rounded corners + side borders for an edge-to-edge table. */
  fullBleed?: boolean
  /** Pin the header while the body scrolls (pair with a `max-h-*` className). */
  stickyHeader?: boolean
  /** Switch to fixed layout and enable drag/keyboard-resize columns (see `TableHead`). */
  resizable?: boolean
  /** Initial column widths (px) keyed by `columnId` — uncontrolled. */
  defaultColumnWidths?: Record<string, number>
  /** Controlled column widths (px) keyed by `columnId`. Pair with `onColumnWidthsChange`. */
  columnWidths?: Record<string, number>
  /** Fires with the full width map on every resize — persist it and feed it back. */
  onColumnWidthsChange?: (widths: Record<string, number>) => void
  /** Non-scrolling region above the table — a tabs strip and/or `TableToolbar`. */
  header?: React.ReactNode
  /** Non-scrolling region below the table — a `TableFooter` (pagination + actions). */
  footer?: React.ReactNode
  /** Classes for the card (border, radius, max-height). */
  className?: string
}

export const TableRoot = ({
  size = 'md',
  headerSize = size,
  fullBleed = false,
  stickyHeader = false,
  resizable = false,
  defaultColumnWidths,
  columnWidths,
  onColumnWidthsChange,
  header,
  footer,
  className,
  children,
  ...props
}: TableRootProps) => {
  const isControlled = columnWidths !== undefined
  const [internalWidths, setInternalWidths] = React.useState<
    Record<string, number>
  >(defaultColumnWidths ?? {})
  const widths = isControlled ? columnWidths : internalWidths
  // Latest widths for the change callback without stale closures.
  const widthsRef = React.useRef(widths)
  widthsRef.current = widths
  const setColumnWidth = React.useCallback(
    (id: string, width: number) => {
      const next = { ...widthsRef.current, [id]: width }
      if (!isControlled) setInternalWidths(next)
      onColumnWidthsChange?.(next)
    },
    [isControlled, onColumnWidthsChange]
  )
  const ctx = React.useMemo(
    () => ({ size, headerSize, stickyHeader, resizable, widths, setColumnWidth }),
    [size, headerSize, stickyHeader, resizable, widths, setColumnWidth]
  )

  // Track horizontal scroll edges so pinned columns only cast a shadow while
  // there's actually content hidden under them.
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [edges, setEdges] = React.useState({ left: false, right: false })
  const updateEdges = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const left = el.scrollLeft > 1
    const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 1
    setEdges((prev) =>
      prev.left === left && prev.right === right ? prev : { left, right }
    )
  }, [])
  React.useEffect(() => {
    updateEdges()
    const el = scrollRef.current
    const ro = el ? new ResizeObserver(updateEdges) : null
    if (el && ro) ro.observe(el)
    window.addEventListener('resize', updateEdges)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', updateEdges)
    }
  }, [updateEdges])

  return (
    <TableContext.Provider value={ctx}>
      <div
        className={cn(
          'flex flex-col overflow-hidden border border-fbui-gray-200 bg-fbui-base-white',
          fullBleed ? 'rounded-none border-x-0' : 'rounded-lg',
          className
        )}
      >
        {header != null && <div className="shrink-0">{header}</div>}
        <div
          ref={scrollRef}
          onScroll={updateEdges}
          data-can-scroll-left={edges.left || undefined}
          data-can-scroll-right={edges.right || undefined}
          className={cn(
            'group/tscroll fbui-scroll min-h-0 w-full flex-1 overflow-x-auto',
            stickyHeader && 'overflow-y-auto'
          )}
        >
          <table
            className={cn(
              // `separate` (not collapse) so pinned cells can render a box-shadow
              // and sticky positioning behaves — row dividers live on the cells.
              'w-full border-separate border-spacing-0 text-left text-fbui-gray-900',
              resizable && 'table-fixed'
            )}
            {...props}
          >
            {children}
          </table>
        </div>
        {footer != null && <div className="shrink-0">{footer}</div>}
      </div>
    </TableContext.Provider>
  )
}
TableRoot.displayName = 'TableRoot'

export const TableHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <TableSectionContext.Provider value="head">
    <thead className={cn('bg-fbui-base-white', className)} {...props} />
  </TableSectionContext.Provider>
)
TableHeader.displayName = 'TableHeader'

export const TableBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <TableSectionContext.Provider value="body">
    {/* The last row sits on the container border — drop its cells' divider. */}
    <tbody
      className={cn('[&>tr:last-child>td]:border-b-0', className)}
      {...props}
    />
  </TableSectionContext.Provider>
)
TableBody.displayName = 'TableBody'

export interface TableRowProps
  extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Tint the row with the brand accent (selected/active state). */
  selected?: boolean
}

export const TableRow = ({ className, selected, ...props }: TableRowProps) => {
  const isBody = React.useContext(TableSectionContext) === 'body'
  return (
    <tr
      data-selected={selected || undefined}
      className={cn(
        // Row dividers live on the cells (border-separate ignores <tr> borders).
        isBody && 'transition-colors',
        // A base background keeps `pin`-ned cells (bg-inherit) opaque while the
        // rest of the row scrolls under them.
        isBody &&
          (selected
            ? 'bg-fbui-accent'
            : 'bg-fbui-base-white hover:bg-fbui-gray-50'),
        className
      )}
      {...props}
    />
  )
}
TableRow.displayName = 'TableRow'

export interface TableHeadProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Freeze this column against the left or right edge during horizontal scroll. */
  pin?: Pin
  /** Stable id for resizable width tracking (required to resize this column). */
  columnId?: string
  /** Smallest width (px) when resizing. Default 80. */
  minWidth?: number
  /** Initial width (px) before any resize. */
  defaultWidth?: number
  /** Opt this column out of resizing even when the table is `resizable`. */
  resizable?: boolean
  /** One-line label with ellipsis (default). Set `false` for control cells. */
  truncate?: boolean
  /** Render a sort affordance and make the label a sort button. */
  sortable?: boolean
  /** Current sort direction for this column (`false` = unsorted). */
  sortDirection?: 'asc' | 'desc' | false
  /** Called when a sortable header is activated. */
  onSort?: () => void
}

export const TableHead = ({
  className,
  pin,
  columnId,
  minWidth = DEFAULT_MIN_WIDTH,
  defaultWidth,
  resizable: resizableProp,
  truncate = true,
  sortable = false,
  sortDirection = false,
  onSort,
  style,
  children,
  ...props
}: TableHeadProps) => {
  const { headerSize, stickyHeader, resizable, widths, setColumnWidth } =
    React.useContext(TableContext)
  // Pinned columns hold a fixed width; everything else is resizable by default.
  const canResize =
    resizable && columnId != null && (resizableProp ?? pin == null)
  const width =
    columnId != null ? (widths[columnId] ?? defaultWidth) : undefined

  // Resize drag: coalesce pointermoves to one width update per frame (rAF), and
  // keep a teardown in a ref so an unmount mid-drag removes the document
  // listeners even if pointerup never fires.
  const rafRef = React.useRef<number | undefined>(undefined)
  const dragCleanupRef = React.useRef<(() => void) | null>(null)
  React.useEffect(() => () => dragCleanupRef.current?.(), [])

  const startResize = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!columnId) return
    e.preventDefault()
    e.stopPropagation()
    const th = e.currentTarget.closest('th')
    const startX = e.clientX
    const startWidth = th?.getBoundingClientRect().width ?? minWidth
    let latestX = startX
    const apply = () => {
      rafRef.current = undefined
      setColumnWidth(columnId, Math.max(minWidth, startWidth + latestX - startX))
    }
    const onMove = (ev: PointerEvent) => {
      latestX = ev.clientX
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(apply)
    }
    const stop = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = undefined
      }
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', stop)
      dragCleanupRef.current = null
    }
    dragCleanupRef.current = stop
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', stop)
  }

  // Keyboard resize: ←/→ nudge by 8px (40px with Shift), floored at minWidth.
  const onResizeKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (!columnId || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return
    e.preventDefault()
    const step = e.shiftKey ? 40 : 8
    const th = e.currentTarget.closest('th')
    const current =
      widths[columnId] ?? th?.getBoundingClientRect().width ?? minWidth
    const delta = e.key === 'ArrowLeft' ? -step : step
    setColumnWidth(columnId, Math.max(minWidth, current + delta))
  }

  const ariaSort = sortable
    ? sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : 'none'
    : undefined

  // A dedicated sort glyph (not a dropdown chevron): neutral up/down when idle,
  // directional once sorted.
  const SortIcon =
    sortDirection === 'asc'
      ? SortAscending
      : sortDirection === 'desc'
        ? SortDescending
        : ArrowsDownUp

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      style={{ width, minWidth, ...style }}
      className={cn(
        'group/th relative border-b border-fbui-gray-200 text-left align-middle text-xs font-medium text-fbui-text-secondary',
        truncate ? 'truncate' : 'whitespace-nowrap',
        (stickyHeader || pin != null) && 'bg-fbui-base-white',
        // A pinned header must outrank the merely-sticky-top headers, or a
        // scrolling column covers the frozen column at the matching z-index.
        pin != null ? 'z-30' : stickyHeader ? 'z-20' : '',
        stickyHeader && 'sticky top-0',
        pinClass(pin),
        pinShadow(pin),
        sizeCfg[headerSize].head,
        className
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className="group/sort -mx-1 flex max-w-full items-center gap-1 rounded px-1 outline-none transition-colors hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
        >
          <span className="truncate">{children}</span>
          <SortIcon
            weight="bold"
            aria-hidden="true"
            className={cn(
              'size-3.5 shrink-0',
              sortDirection
                ? 'text-fbui-gray-700'
                : 'opacity-0 group-hover/sort:opacity-50'
            )}
          />
        </button>
      ) : (
        children
      )}
      {canResize && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize column"
          aria-valuenow={width}
          aria-valuemin={minWidth}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={onResizeKeyDown}
          className="group/resize absolute right-0 top-0 z-10 flex h-full w-2 cursor-col-resize touch-none select-none items-center justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
        >
          <span className="h-1/2 w-0.5 rounded-full bg-transparent transition-colors group-hover/th:bg-fbui-gray-300 group-hover/resize:!bg-fbui-basil-700 group-focus-visible/resize:!bg-fbui-basil-700" />
        </span>
      )}
    </th>
  )
}
TableHead.displayName = 'TableHead'

export interface TableCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Freeze this column against the left or right edge (match the `TableHead`). */
  pin?: Pin
  /**
   * One-line with an ellipsis when the column is constrained (default). Set
   * `false` for content that must show in full — dates/times, deals or deal IDs.
   */
  truncate?: boolean
}

export interface TableToolbarProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Left side — typically a `SearchBar` (falls back to `children`). */
  search?: React.ReactNode
  /** Right side — tool buttons (filter, columns, sort, …). */
  actions?: React.ReactNode
}

/**
 * A header-row bar above the table for search + tool buttons. Pass it to
 * `TableRoot`'s `header` (optionally beneath a tabs strip).
 */
export const TableToolbar = ({
  search,
  actions,
  className,
  children,
  ...props
}: TableToolbarProps) => (
  <div
    className={cn(
      'flex items-center gap-2 border-y border-fbui-gray-200 bg-fbui-gray-50 px-3 py-2',
      className
    )}
    {...props}
  >
    <div className="min-w-0 flex-1">{search ?? children}</div>
    {actions != null && (
      <div className="flex shrink-0 items-center gap-2">{actions}</div>
    )}
  </div>
)
TableToolbar.displayName = 'TableToolbar'

export interface TableFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Right-aligned actions (e.g. add / export buttons). */
  actions?: React.ReactNode
  /** Left-aligned content (e.g. a result count). */
  leading?: React.ReactNode
}

/**
 * The table footer bar — pagination centered, optional leading text and trailing
 * actions. Pass it to `TableRoot`'s `footer`.
 */
export const TableFooter = ({
  actions,
  leading,
  className,
  children,
  ...props
}: TableFooterProps) => (
  <div
    className={cn(
      'grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-t border-fbui-gray-200 bg-fbui-base-white px-3 py-2',
      className
    )}
    {...props}
  >
    <div className="justify-self-start text-xs text-fbui-text-secondary">
      {leading}
    </div>
    <div className="justify-self-center">{children}</div>
    <div className="flex items-center gap-2 justify-self-end">{actions}</div>
  </div>
)
TableFooter.displayName = 'TableFooter'

export const TableCell = ({
  className,
  pin,
  truncate = true,
  ...props
}: TableCellProps) => {
  const { size } = React.useContext(TableContext)
  return (
    <td
      className={cn(
        'border-b border-fbui-gray-200 align-middle text-fbui-gray-900',
        // Cells stay on one line; default truncates with an ellipsis, opt-outs
        // (dates, deal IDs) just don't wrap.
        truncate ? 'truncate' : 'whitespace-nowrap',
        // DS2026 entity links — brand green, no underline.
        '[&_a]:font-medium [&_a]:text-fbui-basil-700 [&_a:hover]:text-fbui-basil-800',
        // Inherit the row's background so a pinned cell stays opaque over scroll.
        pin != null && 'z-10 bg-inherit',
        pinClass(pin),
        pinShadow(pin),
        sizeCfg[size].cell,
        className
      )}
      {...props}
    />
  )
}
TableCell.displayName = 'TableCell'
